import React, { useState, useEffect } from 'react';
import { db, collection, getDocs } from '../firebase';
import { Client, Employee, ServiceSchedule, ServiceScheduleItem, Settings } from '../types';
import { EmployeeCategorySelector } from './EmployeeCategorySelector';
import { Plus, Trash2, Printer, Save, X, Calendar, Building2, Clock, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';

interface ServiceScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  clients: Client[];
  employees: Employee[];
  onSave: (schedule: Omit<ServiceSchedule, 'id' | 'createdAt'>) => Promise<void>;
  editingSchedule?: ServiceSchedule | null;
  settings: Settings | null;
  onPrintPDF: (schedule: ServiceSchedule) => void;
}

export const ServiceScheduleModal: React.FC<ServiceScheduleModalProps> = ({
  isOpen,
  onClose,
  clients,
  employees,
  onSave,
  editingSchedule,
  settings,
  onPrintPDF
}) => {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [quarterTitle, setQuarterTitle] = useState('');
  const [scheduleDate, setScheduleDate] = useState(format(new Date(), 'dd/MM/yyyy'));
  const [items, setItems] = useState<ServiceScheduleItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  useEffect(() => {
    if (editingSchedule) {
      setSelectedClientId(editingSchedule.clientId || '');
      setClientName(editingSchedule.clientName || '');
      setQuarterTitle(editingSchedule.quarterTitle || '');
      setScheduleDate(editingSchedule.scheduleDate || format(new Date(), 'dd/MM/yyyy'));
      setItems(editingSchedule.items || []);
    } else {
      setSelectedClientId('');
      setClientName('');
      const currentYear = new Date().getFullYear();
      setQuarterTitle(`QUARTERLY AIR CONDITIONERS SERVICE SCHEDULE FOR ${format(new Date(), 'MMMM').toUpperCase()} ${currentYear}`);
      setScheduleDate(format(new Date(), 'dd/MM/yyyy'));
      setItems([]);
    }
  }, [editingSchedule, isOpen]);

  const handleClientChange = async (cId: string) => {
    setSelectedClientId(cId);
    if (!cId) {
      setClientName('');
      setItems([]);
      return;
    }

    const selectedClient = clients.find(c => c.id === cId);
    if (!selectedClient) return;

    setClientName(selectedClient.name);
    setLoadingBranches(true);

    try {
      // Fetch subcollection branches
      const branchSnap = await getDocs(collection(db, 'clients', cId, 'branches'));
      const subBranches = branchSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      let allBranches: { name: string; acQuantity?: number }[] = [];

      if (subBranches.length > 0) {
        allBranches = subBranches.map(b => ({ name: (b as any).name, acQuantity: (b as any).acQuantity }));
      } else if (selectedClient.branches && selectedClient.branches.length > 0) {
        allBranches = selectedClient.branches.map((b: any) => ({ name: b.name, acQuantity: b.acQuantity }));
      }

      if (allBranches.length > 0) {
        const scheduleItems: ServiceScheduleItem[] = allBranches.map((b, idx) => ({
          sn: idx + 1,
          branchName: b.name,
          noOfAcs: b.acQuantity || 0,
          noOfDays: 1,
          startingDate: format(new Date(), 'dd/MM/yyyy'),
          assignedTechnicianIds: [],
          assignedTechnicianNames: '',
          status: 'PENDING',
          reportReceived: 'NO',
          smemoReceived: 'NO'
        }));
        setItems(scheduleItems);
      } else {
        // Fallback row if no branches found
        setItems([{
          sn: 1,
          branchName: 'Main Branch',
          noOfAcs: 1,
          noOfDays: 1,
          startingDate: format(new Date(), 'dd/MM/yyyy'),
          assignedTechnicianIds: [],
          assignedTechnicianNames: '',
          status: 'PENDING',
          reportReceived: 'NO',
          smemoReceived: 'NO'
        }]);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load client branches.');
    } finally {
      setLoadingBranches(false);
    }
  };

  const handleAddItem = () => {
    const newItem: ServiceScheduleItem = {
      sn: items.length + 1,
      branchName: '',
      noOfAcs: 1,
      noOfDays: 1,
      startingDate: format(new Date(), 'dd/MM/yyyy'),
      assignedTechnicianIds: [],
      assignedTechnicianNames: '',
      status: 'PENDING',
      reportReceived: 'NO',
      smemoReceived: 'NO'
    };
    setItems([...items, newItem]);
  };

  const handleRemoveItem = (index: number) => {
    const updated = items.filter((_, i) => i !== index).map((item, idx) => ({
      ...item,
      sn: idx + 1
    }));
    setItems(updated);
  };

  const handleItemChange = (index: number, field: keyof ServiceScheduleItem, value: any) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId) {
      toast.error('Please select a client.');
      return;
    }
    if (items.length === 0) {
      toast.error('Please add at least one branch schedule item.');
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        clientId: selectedClientId,
        clientName,
        quarterTitle,
        scheduleDate,
        items
      });
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save service schedule.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6 overflow-y-auto bg-slate-900/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        className="relative w-full max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden my-auto max-h-[92vh] flex flex-col"
      >
        {/* Modal Header */}
        <div className="p-6 bg-gradient-to-r from-blue-900 to-indigo-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/10 rounded-2xl border border-white/20">
              <Calendar className="w-6 h-6 text-blue-200" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{editingSchedule ? 'Edit Service Schedule' : 'Generate Client Service Schedule'}</h2>
              <p className="text-xs text-blue-200">Landscape Service Schedule generator with Technicians categorization & Letterhead PDF</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-xl transition-colors text-white/80 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* Top Info Bar */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-5 bg-slate-50 border border-slate-200/80 rounded-2xl">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                <Building2 className="w-4 h-4 text-blue-800" />
                <span>Select Client *</span>
              </label>
              <select
                required
                value={selectedClientId}
                onChange={(e) => handleClientChange(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-800/20"
              >
                <option value="">-- Choose Client --</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5 md:col-span-2">
              <label className="text-xs font-bold text-slate-700">Schedule Title / Period *</label>
              <input
                required
                type="text"
                value={quarterTitle}
                onChange={(e) => setQuarterTitle(e.target.value)}
                placeholder="e.g. QUARTERLY AIR CONDITIONERS SERVICE SCHEDULE FOR JULY- SEPTEMBER 2026 – THIRD QUARTER OF 2026"
                className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-800/20"
              />
            </div>

            <div className="space-y-1.5 md:col-span-3">
              <label className="text-xs font-bold text-slate-700">Schedule Date Label</label>
              <input
                type="text"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                placeholder="e.g. 9TH JULY 2026"
                className="w-full md:w-64 px-3.5 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-800/20"
              />
            </div>
          </div>

          {/* Schedule Table Items */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider">Branches & Service Details</h3>
                {loadingBranches && <span className="text-xs text-blue-600 font-bold animate-pulse">Loading client branches...</span>}
              </div>
              <button
                type="button"
                onClick={handleAddItem}
                className="px-3 py-1.5 bg-blue-50 text-blue-900 border border-blue-200 rounded-xl text-xs font-bold hover:bg-blue-100 transition-all flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add Branch Row
              </button>
            </div>

            <div className="border border-slate-200 rounded-2xl overflow-x-auto shadow-xs">
              <table className="w-full text-left border-collapse min-w-[1000px]">
                <thead>
                  <tr className="bg-blue-900 text-white text-[11px] uppercase tracking-wider font-bold">
                    <th className="p-3 text-center w-12 border-r border-blue-800">S/N</th>
                    <th className="p-3 border-r border-blue-800 min-w-[180px]">Branch</th>
                    <th className="p-3 text-center border-r border-blue-800 w-24">AC Qty</th>
                    <th className="p-3 text-center border-r border-blue-800 w-24">Days</th>
                    <th className="p-3 border-r border-blue-800 w-32">Start Date</th>
                    <th className="p-3 border-r border-blue-800 min-w-[240px]">Assigned Technician(s)</th>
                    <th className="p-3 border-r border-blue-800 w-32">Status</th>
                    <th className="p-3 text-center border-r border-blue-800 w-24">Report Recv</th>
                    <th className="p-3 text-center border-r border-blue-800 w-24">Memo Recv</th>
                    <th className="p-3 text-center w-12">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 text-xs font-semibold">
                  {items.map((item, index) => (
                    <tr key={index} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 text-center font-bold text-slate-500 bg-slate-50/50 border-r border-slate-200">{item.sn}</td>
                      <td className="p-2 border-r border-slate-200">
                        <input
                          type="text"
                          value={item.branchName}
                          onChange={(e) => handleItemChange(index, 'branchName', e.target.value)}
                          placeholder="Branch Name"
                          className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-800/20"
                        />
                      </td>
                      <td className="p-2 border-r border-slate-200">
                        <input
                          type="number"
                          min="0"
                          value={item.noOfAcs}
                          onChange={(e) => handleItemChange(index, 'noOfAcs', Number(e.target.value))}
                          className="w-full px-2 py-1.5 text-center bg-white border border-slate-200 rounded-lg text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-800/20"
                        />
                      </td>
                      <td className="p-2 border-r border-slate-200">
                        <input
                          type="number"
                          min="1"
                          value={item.noOfDays}
                          onChange={(e) => handleItemChange(index, 'noOfDays', Number(e.target.value))}
                          className="w-full px-2 py-1.5 text-center bg-white border border-slate-200 rounded-lg text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-800/20"
                        />
                      </td>
                      <td className="p-2 border-r border-slate-200">
                        <input
                          type="text"
                          value={item.startingDate}
                          onChange={(e) => handleItemChange(index, 'startingDate', e.target.value)}
                          placeholder="dd/mm/yyyy"
                          className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-800/20"
                        />
                      </td>
                      <td className="p-2 border-r border-slate-200">
                        <EmployeeCategorySelector
                          employees={employees}
                          isMulti={true}
                          selectedIds={item.assignedTechnicianIds || []}
                          onChangeMulti={(ids, names) => {
                            const updated = [...items];
                            updated[index] = {
                              ...updated[index],
                              assignedTechnicianIds: ids,
                              assignedTechnicianNames: names
                            };
                            setItems(updated);
                          }}
                          defaultCategory="Technician"
                          placeholder="Select Technician(s)"
                        />
                        {item.assignedTechnicianNames && (
                          <div className="mt-1 text-[10px] font-bold text-blue-900 bg-blue-50/80 p-1 rounded border border-blue-100 whitespace-pre-line">
                            {item.assignedTechnicianNames}
                          </div>
                        )}
                      </td>
                      <td className="p-2 border-r border-slate-200">
                        <select
                          value={item.status}
                          onChange={(e) => handleItemChange(index, 'status', e.target.value)}
                          className={`w-full px-2 py-1.5 rounded-lg text-xs font-bold border focus:outline-none ${
                            item.status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-800 border-emerald-300' :
                            item.status === 'IN PROGRESS' ? 'bg-amber-50 text-amber-800 border-amber-300' :
                            'bg-rose-50 text-rose-800 border-rose-300'
                          }`}
                        >
                          <option value="PENDING">PENDING</option>
                          <option value="IN PROGRESS">IN PROGRESS</option>
                          <option value="COMPLETED">COMPLETED</option>
                        </select>
                      </td>
                      <td className="p-2 border-r border-slate-200">
                        <select
                          value={item.reportReceived}
                          onChange={(e) => handleItemChange(index, 'reportReceived', e.target.value)}
                          className="w-full px-1.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-center focus:outline-none"
                        >
                          <option value="NO">NO</option>
                          <option value="YES">YES</option>
                        </select>
                      </td>
                      <td className="p-2 border-r border-slate-200">
                        <select
                          value={item.smemoReceived}
                          onChange={(e) => handleItemChange(index, 'smemoReceived', e.target.value)}
                          className="w-full px-1.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-center focus:outline-none"
                        >
                          <option value="NO">NO</option>
                          <option value="YES">YES</option>
                        </select>
                      </td>
                      <td className="p-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove row"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td colSpan={10} className="text-center py-8 text-slate-400 italic">
                        No schedule items yet. Select a client above or click "Add Branch Row".
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Form Actions */}
          <div className="pt-4 border-t border-slate-200 flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={() => {
                if (selectedClientId && items.length > 0) {
                  onPrintPDF({
                    id: 'preview',
                    clientId: selectedClientId,
                    clientName,
                    quarterTitle,
                    scheduleDate,
                    items,
                    createdAt: null as any
                  });
                } else {
                  toast.error('Please select a client and branches before printing preview.');
                }
              }}
              className="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-2xl font-bold hover:bg-slate-200 transition-all flex items-center gap-2"
            >
              <Printer className="w-4 h-4 text-blue-900" /> Preview Landscape PDF
            </button>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 border border-slate-300 rounded-2xl font-bold text-slate-600 hover:bg-slate-50 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-6 py-2.5 bg-blue-900 text-white rounded-2xl font-bold hover:bg-blue-800 transition-all flex items-center gap-2 shadow-lg shadow-blue-900/20 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{isSaving ? 'Saving...' : 'Save Service Schedule'}</span>
              </button>
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
